// Resources and files (AdminResources.dc.html), worked out from the rows data.js reads: which products
// hold resources, the names the board gives them, each item's line and opens line, the order writes,
// and what a draft in the editor needs before it can be saved. Pure: no network and no React, only
// format.js, so a test imports it as it is. Every date is UK time.
import { dayMonth } from '../../lib/format.js'
import { vimeoLinkFrom } from '../../products/mentorship/model.js'
import { parseVimeo } from '../../free/vimeo.js'

const DAY = 86400000

// The product types that have a Resources page (design 5.4; the 1-2-1's files stay in its own space,
// and linked and webinar products hold none)
export const RESOURCE_TYPES = ['live_cohort', 'mastermind', 'on_demand', 'masterclass', 'resource_pack']

// The 5 kinds (decision 8), as the board names them
export const KINDS = ['file', 'tool', 'link', 'text', 'video']
export const KIND_LABEL = { file: 'File', tool: 'Online tool', link: 'Link', text: 'Text to copy', video: 'Vimeo video' }

// The board's names for the products it draws: the chip, and the short name in "Also in the
// Accelerator" and the "Also show it in" chips. Any other product goes by its own name.
const NAMES = {
  'funding-engine': { chip: 'The Funding Engine', short: 'The Funding Engine' },
  accelerator: { chip: 'The Property Accelerator', short: 'the Accelerator' },
  poyt: { chip: 'Profit On Your Terms', short: 'Profit On Your Terms' },
  cashflow: { chip: 'Property Cashflow For Life', short: 'PCFL' },
  masterclass: { chip: 'Masterclass', short: 'the masterclass' },
  '21-ways': { chip: '21 Ways guide, free', short: 'the 21 Ways guide' },
}

export function chipName(p) {
  if (!p) return ''
  if (NAMES[p.slug]) return NAMES[p.slug].chip
  if (p.type === 'masterclass') return 'Masterclass'
  return p.access_kind === 'free' ? `${p.name}, free` : p.name
}
export function shortName(p) {
  if (!p) return ''
  return NAMES[p.slug]?.short || p.name
}
// "1 item", "3 items"
export const itemsWord = n => `${n} ${n === 1 ? 'item' : 'items'}`
// "1 Sep"
export const dayMonthOf = t => dayMonth(t)

export const capitalise = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// "a", "a and b", "a, b and c"
export function listJoin(words) {
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

// The products Resources shows, in the catalogue's order (paid, then free): those that can hold
// resources and are in the hub. A product not moved in (POYT and the Accelerator on live in phase 1)
// is left out: its files stay on its own site until it moves (the stage 5 critic's missed 2).
export function resourceProducts(catalogue) {
  return (catalogue || []).filter(p => p.moved_in && RESOURCE_TYPES.includes(p.type))
}

// Where a member sees it: the product's Resources, or Free resources for a free item with no path
export function memberPath(p) {
  return p?.path ? `${p.path}/resources` : '/free-resources'
}

// The weeks "After a session" offers: a product with live sessions (a live cohort course or a
// mastermind) and numbered modules. An on-demand course has modules but no sessions, so none.
export function weeksOf(product, modules) {
  if (!product || !['live_cohort', 'mastermind'].includes(product.type)) return []
  return (modules || []).filter(m => m.product_id === product.id && Number.isInteger(m.number))
    .sort((a, b) => a.sort_order - b.sort_order || a.number - b.number)
}

const byOrder = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.id).localeCompare(String(b.id))
export const sortByOrder = list => [...(list || [])].sort(byOrder)

const isRemoved = pl => Boolean(pl.removed_at)

// Removed in the last 30 days, which Put it back can still bring back
export function recentlyRemoved(pl, nowMs) {
  return isRemoved(pl) && nowMs - Date.parse(pl.removed_at) <= 30 * DAY
}

// The title and line members see in this product: the placement's own, else the item's
export const titleOf = (pl, item) => (pl?.title_override && pl.title_override.trim()) || item?.title || ''
export const sublineOf = (pl, item) => (pl?.subline_override && pl.subline_override.trim()) || item?.subline || ''

// "Opens after the Week 2 session", "Opens on 1 Nov", "Open straight away"
export function releaseLine(pl, { modules = [], sessions = [] } = {}) {
  if (pl.release_kind === 'after_session') {
    const session = pl.release_session_id ? sessions.find(s => s.id === pl.release_session_id) : null
    const moduleId = pl.release_module_id || session?.module_id
    const mod = moduleId ? modules.find(m => m.id === moduleId) : null
    if (mod) return `Opens after the ${mod.label} session`
    if (session?.starts_at) return `Opens after the session on ${dayMonth(session.starts_at)}`
    return 'Opens after a live session'
  }
  if (pl.release_kind === 'on_date' && pl.release_at) return `Opens on ${dayMonth(pl.release_at)}`
  return 'Open straight away'
}

// The item's file, and whether it has been replaced: { fileName, updatedAt }
export function fileFacts(item, versions) {
  if (!item || !['file', 'tool'].includes(item.kind)) return { fileName: null, updatedAt: null }
  const mine = (versions || []).filter(v => v.item_id === item.id)
  const current = mine.find(v => v.id === item.current_version_id) || null
  return { fileName: current?.file_name || null, updatedAt: current && mine.length > 1 ? current.uploaded_at : null }
}

// The grey line under a row's title, as the board writes it:
// "File, pillar-canvas.pdf. Fillable PDF. Opens after the Week 2 session. Yours for life. Also in the Accelerator"
export function metaLine({ pl, item, fileName, alsoIn = [], modules, sessions }) {
  const parts = [KIND_LABEL[item.kind] + (fileName ? `, ${fileName}` : '')]
  const sub = sublineOf(pl, item)
  if (sub) parts.push(sub)
  parts.push(releaseLine(pl, { modules, sessions }))
  if (pl.yours_for_life) parts.push('Yours for life')
  if (alsoIn.length) parts.push(`Also in ${listJoin(alsoIn.map(shortName))}`)
  return parts.map(p => String(p).trim().replace(/\.+$/, '')).filter(Boolean).join('. ')
}

// The opens line (decision 11), from hub.resource_open_counts' row for the placement:
// "No members yet", "Not open to members yet", "Not opened yet" (in its first 14 days, or just
// added), or "Opened by n of N", with ". Worth a nudge" when fewer than half have opened it.
// null when the counts didn't load, so the row shows no line rather than a wrong one.
export function opensLine(count, { loaded = true, nowMs = Date.now() } = {}) {
  if (!loaded) return null
  if (!count) return { text: 'Not opened yet', warn: false }
  if (!count.members) return { text: 'No members yet', warn: false }
  const released = count.released_at ? Date.parse(count.released_at) : null
  if (released === null || released > nowMs) return { text: 'Not open to members yet', warn: false }
  if (!count.opened && nowMs - released < 14 * DAY) return { text: 'Not opened yet', warn: false }
  const warn = count.opened < count.members / 2
  return { text: `Opened by ${count.opened} of ${count.members}${warn ? '. Worth a nudge' : ''}`, warn }
}

// Moving 1 row up (-1) or down (+1) in an ordered list: the sort_order writes it takes. 2 rows swap
// their numbers when every number in the list is different; otherwise the whole list is numbered
// 1, 2, 3 in its new order, so 2 rows with the same number can still change places.
export function reorderWrites(list, index, dir) {
  const to = index + dir
  if (to < 0 || to >= list.length || index < 0 || index >= list.length) return []
  const orders = list.map(r => r.sort_order ?? 0)
  if (new Set(orders).size === orders.length) {
    return [{ id: list[index].id, sort_order: orders[to] }, { id: list[to].id, sort_order: orders[index] }]
  }
  const next = [...list]
  const [moved] = next.splice(index, 1)
  next.splice(to, 0, moved)
  return next.map((r, i) => ({ id: r.id, sort_order: i + 1 })).filter((w, i) => (next[i].sort_order ?? 0) !== w.sort_order)
}

// The next sort_order at the end of a list
export const nextOrder = list => (list || []).reduce((n, r) => Math.max(n, r.sort_order ?? 0), 0) + 1

// ---- The editor's draft -------------------------------------------------------------------------

// A link out (https), or a page in the hub (/masterclass), as resource_item_bodies.link_url takes it
export function linkFrom(input) {
  const s = String(input || '').trim()
  if (/^https:\/\/[^\s]+$/i.test(s)) return s
  if (/^\/[a-z0-9/_-]*$/.test(s)) return s
  return null
}

// "Vimeo video 1223397816 found. It plays inside the hub." for a link or embed code that names one
export function vimeoNote(input) {
  const link = vimeoLinkFrom(input)
  const v = link ? parseVimeo(link) : null
  return v ? `Vimeo video ${v.id} found. It plays inside the hub.` : 'The video stays on Vimeo. Paste its link or embed code.'
}

const TOOL_TYPES = ['text/html']
const FILE_TYPES = [
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/zip', 'application/x-zip-compressed',
]
const BY_EXT = {
  pdf: 'application/pdf', doc: 'application/msword', docx: FILE_TYPES[2], xls: 'application/vnd.ms-excel', xlsx: FILE_TYPES[4],
  ppt: 'application/vnd.ms-powerpoint', pptx: FILE_TYPES[6], jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  webp: 'image/webp', gif: 'image/gif', zip: 'application/zip', html: 'text/html', htm: 'text/html',
}
export const MAX_BYTES = 52428800 // the resources bucket's 50 MB

// A chosen file's type, as the browser gives it or from its name when the browser gives none
export function fileType(file) {
  const t = String(file?.type || '').split(';')[0].trim().toLowerCase()
  if (t) return t
  const ext = String(file?.name || '').split('.').pop().toLowerCase()
  return BY_EXT[ext] || ''
}

// What the file box accepts for a kind
export const ACCEPT = {
  file: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.gif,.zip',
  tool: '.html,.htm,text/html',
}

// Whether a chosen file can go in: null, or the line to show. The words are sign-upload's own
// (docs/data-model.md 10.4.1), checked here first so a wrong file isn't sent at all.
export function fileProblem(kind, file) {
  if (!file) return null
  const type = fileType(file)
  if (kind === 'tool' && !TOOL_TYPES.includes(type)) return 'An online tool has to be an HTML file.'
  if (kind === 'file' && !FILE_TYPES.includes(type)) {
    return "That kind of file can't be added. Add a PDF, a Word, Excel or PowerPoint file, a photo (JPEG, PNG, WebP or GIF) or a zip file."
  }
  if (file.size > MAX_BYTES) return 'That file is bigger than 50 MB. Add a smaller one.'
  if (!file.size) return "That file is empty. Add it again."
  return null
}

// A new item's draft, in a section (the board's blank: a file, open straight away, marked new)
export function blankDraft(sectionId, weeks) {
  return {
    kind: 'file', title: '', subline: '', sectionId, release: 'now', moduleId: weeks[0]?.id || null, date: '',
    life: false, isNew: true, also: [], link: '', text: '', file: null,
  }
}

// An existing item's draft, from its placement, item, body and the other products it's in
export function draftFrom({ pl, item, body, alsoIn, weeks, nowMs, sessions = [] }) {
  // a release on 1 named session (the board can't draw it) shows as that session's week
  const sessionModuleId = pl.release_session_id ? sessions.find(x => x.id === pl.release_session_id)?.module_id || null : null
  const releaseModule = pl.release_module_id || sessionModuleId
  return {
    kind: item.kind,
    title: titleOf(pl, item),
    subline: sublineOf(pl, item),
    sectionId: pl.section_id,
    release: pl.release_kind === 'after_session' ? 'after_session' : pl.release_kind === 'on_date' ? 'on_date' : 'now',
    moduleId: releaseModule || weeks[0]?.id || null,
    date: pl.release_kind === 'on_date' && pl.release_at ? ukDay(pl.release_at) : '',
    life: Boolean(pl.yours_for_life),
    isNew: Boolean(pl.new_until && Date.parse(pl.new_until) > nowMs),
    also: alsoIn.map(p => p.id),
    sessionModuleId,
    link: item.kind === 'link' ? body?.link_url || '' : item.kind === 'video' ? body?.vimeo_url || '' : '',
    text: item.kind === 'text' ? body?.text_body || '' : '',
    file: null,
  }
}

// A London day as YYYY-MM-DD
function ukDay(t) {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date(t))
  const get = type => parts.find(p => p.type === type)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}

// What the draft still needs before it can be saved: null, or the line to show
export function draftProblem(draft, { mode, hasFile = false } = {}) {
  if (!String(draft.title || '').trim()) return 'Give it a title first.'
  if (['file', 'tool'].includes(draft.kind)) {
    if (mode === 'add' && !draft.file) return 'Choose a file first.'
    if (!draft.file && !hasFile) return 'Choose a file first.'
    const problem = fileProblem(draft.kind, draft.file)
    if (problem) return problem
  }
  if (draft.kind === 'link' && !linkFrom(draft.link)) return 'Add the address first: a link starting https://, or a page in the hub starting /.'
  if (draft.kind === 'video' && !vimeoLinkFrom(draft.link)) return 'Paste a Vimeo link or its embed code first.'
  if (draft.kind === 'text' && !String(draft.text || '').trim()) return 'Paste the text members copy first.'
  if (draft.release === 'on_date' && !/^\d{4}-\d{2}-\d{2}$/.test(draft.date || '')) return 'Pick the date it opens.'
  if (draft.release === 'after_session' && !draft.moduleId) return 'Pick the week it opens after.'
  return null
}

// The placement's release columns for a draft (module_id follows the week, so the item shows on that
// week's page too; the stage 5 map's note)
export function releaseColumns(draft, toIso) {
  if (draft.release === 'after_session') {
    return { release_kind: 'after_session', release_module_id: draft.moduleId, release_session_id: null, release_at: null, module_id: draft.moduleId }
  }
  if (draft.release === 'on_date') {
    return { release_kind: 'on_date', release_module_id: null, release_session_id: null, release_at: toIso(draft.date) }
  }
  return { release_kind: 'now', release_module_id: null, release_session_id: null, release_at: null }
}

// Whether the draft keeps the placement's release as it is (so nothing is written for it, and a rule
// the board can't draw, a named session, stays as it was)
export function releaseUnchanged(draft, pl) {
  if (draft.release !== pl.release_kind) return false
  if (pl.release_kind === 'after_session') return draft.moduleId === (pl.release_module_id || draft.sessionModuleId || null)
  if (pl.release_kind === 'on_date') return Boolean(pl.release_at) && draft.date === ukDay(pl.release_at)
  return true
}

// "Added Pillar canvas. Members who have it see it now." or "Saved Pillar canvas. Opens after the Week 2
// session for members."
// marked: "Mark it new" ticked and hub.bell_will_ring said before the write that the database would
// put it in the bell of everyone it's open to now (stage 6: the bell, not an email, is how members hear;
// the board's " Everyone who has it gets an email." is gone; Adam, 5 Oct, stage 6's batch: never claimed
// for 1 already marked new, or a product that doesn't speak)
export function savedLine(mode, title, pl, ctx, { marked = false } = {}) {
  const when = pl.release_kind === 'now'
    ? (marked ? "Members who have it see it now, and it's in their bell." : 'Members who have it see it now.')
    : `${releaseLine(pl, ctx)} for members.`
  return `${mode === 'add' ? 'Added' : 'Saved'} ${title}. ${when}`
}
